import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { researchToolsApi } from './api';
import { CreateSurveyWorkflow } from './components/CreateSurveyWorkflow';
import { DataAnalysisView } from './components/DataAnalysisView';
import { ExperimentBuilderView } from './components/ExperimentBuilderView';
import { ExperimentRegistryView } from './components/ExperimentRegistryView';
import { ImportSurveyView } from './components/ImportSurveyView';
import { MySurveysDashboard } from './components/MySurveysDashboard';
import { QuickServiceSurveyView } from './components/QuickServiceSurveyView';
import { RespondentPreviewModal } from './components/RespondentPreviewModal';
import { StructureMappingView } from './components/StructureMappingView';
import { SurveyBuilderView } from './components/SurveyBuilderView';
import { SurveyPublishView } from './components/SurveyPublishView';
import './research-tools.css';
import './research-tools-overlay.css';
import type {
  ExperimentDetail,
  QuestionConfig,
  ResearchToolsSummary,
  SurveyDetail,
  SurveyMode,
} from './types';

type ActiveView =
  | 'HOME'
  | 'CREATE_WORKFLOW'
  | 'IMPORT'
  | 'MAPPING'
  | 'BUILDER'
  | 'EXPERIMENT_BUILDER'
  | 'EXPERIMENT_REGISTRY'
  | 'DATA_ANALYSIS'
  | 'MY_SURVEYS'
  | 'QUICK_SURVEY'
  | 'PUBLISH';

const VIEW_CHROME: Record<
  ActiveView,
  { title: string; subtitle: string }
> = {
  HOME: {
    title: 'Программы и инструменты',
    subtitle: 'исследовательские инструменты платформы',
  },
  CREATE_WORKFLOW: {
    title: 'Создать опрос',
    subtitle: 'Опросы · гостевые ссылки · интерфейс респондента',
  },
  IMPORT: {
    title: 'Импорт готового опроса',
    subtitle: 'Файл или текст · затем распознавание структуры',
  },
  MAPPING: {
    title: 'Распознавание структуры',
    subtitle: 'Типы вопросов и сопоставление полей',
  },
  BUILDER: {
    title: 'Конструктор опроса',
    subtitle: 'Вопросы · аудитория · предпросмотр',
  },
  EXPERIMENT_BUILDER: {
    title: 'Провести эксперимент',
    subtitle: 'Стимулы · сценарий · модерация',
  },
  EXPERIMENT_REGISTRY: {
    title: 'Провести эксперимент',
    subtitle: 'Реестр · статусы · гостевые ссылки',
  },
  DATA_ANALYSIS: {
    title: 'Анализ данных через программы',
    subtitle: 'Направления · инструменты · исходный материал',
  },
  MY_SURVEYS: {
    title: 'Мои опросы и ссылки',
    subtitle: 'Черновики · модерация · ответы',
  },
  QUICK_SURVEY: {
    title: 'Быстрый служебный опрос',
    subtitle: 'Команда · черновик · доставка целевой группе',
  },
  PUBLISH: {
    title: 'Публикация и гостевая ссылка',
    subtitle: 'После одобрения — отдельный URL для участников',
  },
};

interface Props {
  onClose?: () => void;
  onBack?: () => void;
  overrideIsOwner?: boolean;
}

type SuperAiSurveyHandoff = {
  superAiSurveyDraft?: Record<string, unknown>;
};

const mapSuperAiDraftToBuilder = (handoff: Record<string, unknown>): Partial<{
  title: string;
  description: string;
  audience: string;
  language: string;
  questions: QuestionConfig[];
}> => {
  const questionsRaw = Array.isArray(handoff.questions) ? handoff.questions : [];
  const langLabel = String(handoff.language ?? 'RU');
  return {
    title: String(handoff.title ?? 'Академический опрос'),
    description: String(handoff.target_audience_desc ?? ''),
    audience: String(handoff.target_audience_desc ?? 'Докторанты'),
    language: langLabel.includes('Қазақ') || langLabel === 'KZ' ? 'KZ' : 'RU',
    questions: questionsRaw.map((q, index) => {
      const row = q as Record<string, unknown>;
      return {
        order_index: typeof row.order_index === 'number' ? row.order_index : index + 1,
        question_type: 'text',
        title: String(row.text ?? row.title ?? `Вопрос ${index + 1}`),
        is_required: Boolean(row.required ?? true),
      };
    }),
  };
};

const TOOL_ICON = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5h16v14H4z M8 9h8 M8 13h5 M17 13h1 M7 17h10" />
  </svg>
);

/** Research tools hub — template research-tools home() + overlay chrome. */
export const ResearchToolsPage: React.FC<Props> = ({
  onClose,
  onBack,
  overrideIsOwner,
}) => {
  const { hasRole } = useAuth();
  const isOwner = overrideIsOwner !== undefined ? overrideIsOwner : hasRole('SYSTEM_OWNER');
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const [activeView, setActiveView] = useState<ActiveView>('HOME');
  const [summary, setSummary] = useState<ResearchToolsSummary>({
    total_tools: 36,
    total_directions: 6,
    surveys_count: 0,
    experiments_count: 0,
    responses_count: 0,
  });

  const [builderData, setBuilderData] = useState<{
    title: string;
    description: string;
    audience: string;
    language: string;
    mode: SurveyMode;
    questions: QuestionConfig[];
  }>({
    title: 'Исследование результативности образовательной программы',
    description: 'Контрольный опрос удовлетворённости и промежуточных итогов исследовательской работы',
    audience: 'Докторанты 1 курса',
    language: 'RU',
    mode: 'ANONYMOUS',
    questions: [],
  });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeSurvey, setActiveSurvey] = useState<SurveyDetail | null>(null);

  useEffect(() => {
    researchToolsApi
      .getSummary()
      .then(setSummary)
      .catch(() => {});
  }, [activeView]);

  useEffect(() => {
    if (searchParams.get('view') !== 'create-survey') return;
    const handoff = (location.state as SuperAiSurveyHandoff | null)?.superAiSurveyDraft;
    if (handoff) {
      const mapped = mapSuperAiDraftToBuilder(handoff);
      setBuilderData((prev) => ({
        ...prev,
        title: mapped.title ?? prev.title,
        description: mapped.description || prev.description,
        audience: mapped.audience ?? prev.audience,
        language: mapped.language ?? prev.language,
        questions: mapped.questions ?? prev.questions,
      }));
      setActiveView(mapped.questions && mapped.questions.length > 0 ? 'BUILDER' : 'CREATE_WORKFLOW');
      return;
    }
    setActiveView('CREATE_WORKFLOW');
  }, [searchParams, location.state]);

  const handleBackToHome = () => {
    setActiveView('HOME');
  };

  const handleClose = onClose ?? onBack ?? (() => undefined);
  const handleBack = () => {
    if (previewOpen) {
      setPreviewOpen(false);
      return;
    }
    if (activeView === 'MAPPING') {
      setActiveView('IMPORT');
      return;
    }
    if (activeView === 'IMPORT' || activeView === 'BUILDER') {
      setActiveView('CREATE_WORKFLOW');
      return;
    }
    if (activeView === 'PUBLISH') {
      setActiveView('MY_SURVEYS');
      return;
    }
    if (activeView === 'EXPERIMENT_BUILDER') {
      setActiveView('EXPERIMENT_REGISTRY');
      return;
    }
    if (activeView !== 'HOME') {
      handleBackToHome();
      return;
    }
    if (onBack) onBack();
    else handleClose();
  };

  const chrome = previewOpen
    ? {
        title: 'Предпросмотр респондента',
        subtitle: 'Точный вид участника до публикации',
      }
    : VIEW_CHROME[activeView];

  const handleOpenQuickSurvey = () => {
    if (!isOwner) {
      alert('Доступ запрещён: Быстрый служебный опрос доступен только владельцу системы.');
      return;
    }
    setActiveView('QUICK_SURVEY');
  };

  const handleAcceptMapping = (
    questions: QuestionConfig[],
    audience: string,
    language: string
  ) => {
    setBuilderData((prev) => ({
      ...prev,
      questions,
      audience,
      language,
    }));
    setPreviewOpen(false);
    setActiveView('BUILDER');
  };

  const handleSaveDraftFromBuilder = async (data: typeof builderData) => {
    setBuilderData(data);
    const created = await researchToolsApi.createSurvey({
      title: data.title,
      description: data.description,
      audience: data.audience,
      language: data.language,
      mode: data.mode,
      questions: data.questions,
    });
    setActiveSurvey(created);
    return created;
  };

  const handleOpenPreview = (data: typeof builderData) => {
    setBuilderData(data);
    setPreviewOpen(true);
  };

  const handleApproveAndPublish = async () => {
    let current = activeSurvey;
    if (!current) {
      current = await researchToolsApi.createSurvey({
        title: builderData.title,
        description: builderData.description,
        audience: builderData.audience,
        language: builderData.language,
        mode: builderData.mode,
        questions: builderData.questions,
      });
    }
    const approved = await researchToolsApi.approveSurvey(current.id);
    setActiveSurvey(approved);
    setPreviewOpen(false);
    setActiveView('PUBLISH');
  };

  const handleExperimentCreated = (_exp: ExperimentDetail) => {
    setActiveView('EXPERIMENT_REGISTRY');
  };

  return (
    <div
      className="research-tools-overlay open"
      role="dialog"
      aria-modal="true"
      aria-label="Программы и инструменты"
    >
      <div className="research-tools-shell">
        <header className="research-tools-top with-back">
          <button type="button" onClick={handleBack}>
            ← Назад
          </button>
          <div>
            <h1>{chrome.title}</h1>
            <p>
              {activeView === 'HOME' && !previewOpen
                ? `${isOwner ? 'Владелец системы' : 'Исследователь'} · ${chrome.subtitle}`
                : chrome.subtitle}
            </p>
          </div>
          <button type="button" onClick={handleClose}>
            Закрыть ×
          </button>
        </header>

        <main className="research-tools-main">
          {previewOpen ? (
            <RespondentPreviewModal
              title={builderData.title}
              description={builderData.description}
              audience={builderData.audience}
              language={builderData.language}
              mode={builderData.mode}
              questions={builderData.questions}
              onBackToEdit={() => setPreviewOpen(false)}
              onApproveAndPublish={handleApproveAndPublish}
            />
          ) : (
            <>
              {activeView === 'HOME' ? (
                <>
                  <section className="research-tools-hero">
                    <div>
                      <small>Zertteu Academy · исследовательский контур</small>
                      <h2>Методы, данные и эксперименты</h2>
                      <p>
                        Отдельный рабочий контур: создать исследовательский опрос, провести
                        эксперимент без регистрации участников или подготовить анализ данных через
                        подходящий инструмент.
                      </p>
                    </div>
                    <div className="research-tools-statline">
                      <span>{summary.total_tools} инструментов</span>
                      <span>{summary.total_directions} направлений анализа</span>
                      <span>{summary.surveys_count} опросов</span>
                    </div>
                  </section>

                  <div className="research-tools-action-grid">
                    <button
                      type="button"
                      className="research-tools-action"
                      style={{ ['--rt-accent' as string]: '#41d2ff' }}
                      onClick={() => setActiveView('CREATE_WORKFLOW')}
                    >
                      <span className="ico">{TOOL_ICON}</span>
                      <h3>Создать опрос</h3>
                      <p>
                        Анкета или исследовательский опрос. После модерации формируется отдельная
                        публичная ссылка для участников без регистрации.
                      </p>
                      <b>Создать →</b>
                    </button>
                    <button
                      type="button"
                      className="research-tools-action"
                      style={{ ['--rt-accent' as string]: '#49ddb7' }}
                      onClick={() => setActiveView('EXPERIMENT_REGISTRY')}
                    >
                      <span className="ico">{TOOL_ICON}</span>
                      <h3>Провести эксперимент</h3>
                      <p>
                        Ассоциативный, педагогический предтест–посттест, экспертная оценка или
                        другой контролируемый сбор ответов.
                      </p>
                      <b>Настроить →</b>
                    </button>
                    <button
                      type="button"
                      className="research-tools-action"
                      style={{ ['--rt-accent' as string]: '#e2ad3a' }}
                      onClick={() => setActiveView('DATA_ANALYSIS')}
                    >
                      <span className="ico">{TOOL_ICON}</span>
                      <h3>Анализ данных через программы</h3>
                      <p>
                        6 направлений и 36 инструментов. Выберите один инструмент или доверьте
                        выбор системе; исходный файл обязателен.
                      </p>
                      <b>Выбрать инструмент →</b>
                    </button>
                    <button
                      type="button"
                      className="research-tools-action"
                      style={{ ['--rt-accent' as string]: '#9e80ff' }}
                      onClick={() => setActiveView('MY_SURVEYS')}
                    >
                      <span className="ico">{TOOL_ICON}</span>
                      <h3>Мои опросы и ссылки</h3>
                      <p>
                        Черновики, модерация, одобренные публичные ссылки и количество собранных
                        ответов.
                      </p>
                      <b>Открыть →</b>
                    </button>
                    {isOwner ? (
                      <button
                        type="button"
                        className="research-tools-action"
                        style={{ ['--rt-accent' as string]: '#ff7b65' }}
                        onClick={handleOpenQuickSurvey}
                      >
                        <span className="ico">{TOOL_ICON}</span>
                        <h3>Быстрый служебный опрос</h3>
                        <p>
                          Экспресс-опрос текстом или голосом, черновик с помощью консультанта и
                          доставка целевой группе обучающихся после подтверждения.
                        </p>
                        <b>Только владелец →</b>
                      </button>
                    ) : null}
                  </div>
                </>
              ) : null}

              {activeView === 'CREATE_WORKFLOW' && (
                <CreateSurveyWorkflow
                  isOwner={isOwner}
                  onSelectImport={() => setActiveView('IMPORT')}
                  onSelectBuilder={() => setActiveView('BUILDER')}
                  onSelectDashboard={() => setActiveView('MY_SURVEYS')}
                  onSelectQuickSurvey={handleOpenQuickSurvey}
                  onBack={handleBackToHome}
                />
              )}

              {activeView === 'IMPORT' && (
                <ImportSurveyView
                  onAcceptMapping={handleAcceptMapping}
                  onOpenMapping={() => setActiveView('MAPPING')}
                  onBack={() => setActiveView('CREATE_WORKFLOW')}
                />
              )}

              {activeView === 'MAPPING' && (
                <StructureMappingView
                  onAccept={handleAcceptMapping}
                  onBack={() => setActiveView('IMPORT')}
                />
              )}

              {activeView === 'BUILDER' && (
                <SurveyBuilderView
                  initialTitle={builderData.title}
                  initialAudience={builderData.audience}
                  initialLanguage={builderData.language}
                  initialMode={builderData.mode}
                  initialQuestions={builderData.questions}
                  onSaveDraft={handleSaveDraftFromBuilder}
                  onOpenPreview={handleOpenPreview}
                  onBack={() => setActiveView('CREATE_WORKFLOW')}
                />
              )}

              {activeView === 'EXPERIMENT_BUILDER' && (
                <ExperimentBuilderView
                  onSuccess={handleExperimentCreated}
                  onBack={() => setActiveView('EXPERIMENT_REGISTRY')}
                />
              )}

              {activeView === 'EXPERIMENT_REGISTRY' && (
                <ExperimentRegistryView
                  isOwner={isOwner}
                  onBack={handleBackToHome}
                  onOpenCreate={() => setActiveView('EXPERIMENT_BUILDER')}
                />
              )}

              {activeView === 'DATA_ANALYSIS' && <DataAnalysisView onBack={handleBackToHome} />}

              {activeView === 'MY_SURVEYS' && (
                <MySurveysDashboard
                  onBack={handleBackToHome}
                  onOpenCreate={() => setActiveView('CREATE_WORKFLOW')}
                  onSelectSurveyToPublish={(survey) => {
                    setActiveSurvey(survey);
                    setActiveView('PUBLISH');
                  }}
                />
              )}

              {activeView === 'QUICK_SURVEY' && isOwner && (
                <QuickServiceSurveyView onBack={handleBackToHome} />
              )}

              {activeView === 'PUBLISH' && activeSurvey && (
                <SurveyPublishView
                  survey={activeSurvey}
                  onOpenGuestAnonymous={() => {
                    window.open(`/guest/survey/${activeSurvey.guest_token}`, '_blank');
                  }}
                  onOpenGuestVerified={() => {
                    window.open(`/guest/survey/${activeSurvey.guest_token}`, '_blank');
                  }}
                  onOpenDashboard={() => setActiveView('MY_SURVEYS')}
                  onBack={handleBackToHome}
                />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};
